(() => {
  'use strict';

  const urls = window.Wavy24AssetUrls || {};
  const scriptPromises = new Map();
  const stylePromises = new Map();

  function loadScript(key, url) {
    if (!url) return Promise.resolve();
    if (scriptPromises.has(key)) return scriptPromises.get(key);

    const existing = document.querySelector(`script[data-wavy24-asset="${key}"]`);
    if (existing) {
      const promise = existing.dataset.loaded === 'true'
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', reject, { once: true });
          });
      scriptPromises.set(key, promise);
      return promise;
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.defer = true;
      script.dataset.wavy24Asset = key;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', () => reject(new Error(`Failed to load ${key}`)), { once: true });
      document.head.appendChild(script);
    });

    scriptPromises.set(key, promise);
    return promise;
  }

  function loadStyle(key, url) {
    if (!url) return Promise.resolve();
    if (stylePromises.has(key)) return stylePromises.get(key);

    const existing = document.querySelector(`link[data-wavy24-asset="${key}"]`);
    if (existing) {
      const promise = existing.sheet
        ? Promise.resolve()
        : new Promise((resolve, reject) => {
            existing.addEventListener('load', resolve, { once: true });
            existing.addEventListener('error', reject, { once: true });
          });
      stylePromises.set(key, promise);
      return promise;
    }

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.dataset.wavy24Asset = key;
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', () => reject(new Error(`Failed to load ${key}`)), { once: true });
      document.head.appendChild(link);
    });

    stylePromises.set(key, promise);
    return promise;
  }

  const api = {
    loadProductForm() {
      if (window.customElements?.get('product-form')) return Promise.resolve();
      return loadScript('product-form', urls.productForm);
    },
    async loadProductInfo() {
      if (window.customElements?.get('product-info')) return;
      await this.loadProductForm();
      await loadScript('product-info', urls.productInfo);
    },
    loadPredictiveSearch() {
      if (window.customElements?.get('predictive-search-drawer')) return Promise.resolve();
      return loadScript('predictive-search', urls.predictiveSearch);
    },
    async loadPhotoSwipe() {
      if (window.PhotoSwipe && window.PhotoSwipeLightbox) return;
      await Promise.all([
        loadStyle('photoswipe-css', urls.photoSwipeCss),
        loadScript('photoswipe-core', urls.photoSwipeCore)
      ]);
      await loadScript('photoswipe-lightbox', urls.photoSwipeLightbox);
    },
    async loadAnimations() {
      if (window.Wavy24AnimationsReady) return;
      await loadScript('foundation-animations', urls.foundationAnimations);
      await loadScript('animation-engine', urls.animationEngine);
      await loadScript('animation-init', urls.animationInit);
      window.Wavy24AnimationsReady = true;
      document.dispatchEvent(new CustomEvent('wavy24:animations-ready'));
    }
  };

  window.Wavy24Assets = api;

  const onReady = () => {
    if (document.querySelector('product-form')) {
      api.loadProductForm().catch(console.warn);
    }
    if (document.querySelector('product-info.main-product-view, product-info.feature-product-view')) {
      api.loadProductInfo().catch(console.warn);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  const warmOnIntent = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-header-search-trigger], search-drawer-wrapper')) {
      api.loadPredictiveSearch().catch(console.warn);
    }
    if (target.closest('button[is="media-lightbox-button"], [data-pswp-index]')) {
      api.loadPhotoSwipe().catch(console.warn);
    }
    if (target.closest('card-quick-view-button, variant-button')) {
      Promise.all([api.loadProductForm(), api.loadProductInfo()]).catch(console.warn);
    }
  };

  document.addEventListener('pointerover', warmOnIntent, { passive: true, capture: true });
  document.addEventListener('focusin', warmOnIntent, { passive: true, capture: true });
  document.addEventListener('touchstart', warmOnIntent, { passive: true, capture: true });

  const scheduleAnimations = () => {
    if (!urls.animationsEnabled) return;

    const run = () => {
      cleanup();
      api.loadAnimations().catch(console.warn);
    };
    const events = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
    const cleanup = () => events.forEach((eventName) => window.removeEventListener(eventName, run));

    if (document.documentElement.classList.contains('shopify-design-mode')) {
      run();
      return;
    }

    events.forEach((eventName) => window.addEventListener(eventName, run, {
      once: true,
      passive: eventName !== 'keydown'
    }));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleAnimations, { once: true });
  } else {
    scheduleAnimations();
  }
})();
